import { cn } from "@repo/design-system/utils";
import { Check, Copy } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

/** How long the "Copied" confirmation stays up, in ms. */
const CONFIRMATION_MS = 2000;

/**
 * Writes `value` to the clipboard, reporting whether it worked.
 *
 * `navigator.clipboard` is unavailable outside a secure context, which is not
 * only a localhost-over-LAN developer problem: the pilot drives salons in from
 * WhatsApp (KAN-16), and in-app webviews are exactly where the modern API is
 * most likely to be missing. So fall back to the legacy `execCommand` path
 * rather than leaving the button silently doing nothing.
 */
async function writeToClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the legacy path below.
  }

  // Selecting the textarea takes focus off whatever the user activated, and a
  // throw from execCommand would otherwise leave that focus stranded in an
  // invisible node - along with the node itself. Both are undone in `finally`.
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const textarea = document.createElement("textarea");

  try {
    textarea.value = value;
    // Keep it off-screen, and out of the way of the page's scroll position.
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
    previouslyFocused?.focus?.();
  }
}

export interface CopyButtonProps {
  /** The exact string written to the clipboard - no label, no formatting. */
  value: string;
  /**
   * What is being copied, for the accessible name ("Copy PayID"). Never shown
   * as visible text; the button is icon-only by design so it can sit beside a
   * value without competing with it.
   */
  label: string;
  className?: string;
}

/**
 * Icon-only button that copies `value` verbatim and confirms it did.
 *
 * Confirmation is announced through an `aria-live` region rather than by
 * swapping the button's accessible name - renaming a control under the cursor
 * of the user who just activated it is disorienting. Success is carried
 * visually by the icon swap, so its message stays screen-reader-only; failure
 * has no icon to lean on, so that message is visible.
 */
export function CopyButton({ value, label, className }: CopyButtonProps) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A copy landing just before unmount would otherwise set state on a gone
  // component once the confirmation expires.
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const handleClick = useCallback(async () => {
    const ok = await writeToClipboard(value);
    setState(ok ? "copied" : "failed");

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setState("idle"), CONFIRMATION_MS);
  }, [value]);

  const copied = state === "copied";

  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <button
        type="button"
        onClick={handleClick}
        aria-label={`Copy ${label}`}
        className={cn(
          "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md",
          "text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2",
          copied && "text-green-600 hover:text-green-600"
        )}
      >
        {copied ? (
          <Check className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Copy className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
      <span
        aria-live="polite"
        className={cn(state === "failed" ? "text-xs text-red-600" : "sr-only")}
      >
        {state === "copied" && `${label} copied`}
        {state === "failed" && "Copy failed"}
      </span>
    </span>
  );
}
